import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faUser,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faBuilding,
    faList,
    faGlobe,
    faUserTag,
    faStickyNote,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faStore,
    faExchangeAlt,
    faMoneyBillWave,
    faCreditCard,
    faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import "./Styles/Form.css";

// Create translator instance
const createSupplierTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Supplier Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        // Use dictionary for common translations first
        const dict = {
            'ur': {
                'Add Supplier': 'سپلائر شامل کریں',
                'Fill in the supplier details below': 'نیچے سپلائر کی تفصیلات بھریں',
                'Supplier Name': 'سپلائر کا نام',
                'Phone Number': 'فون نمبر',
                'Email Address': 'ای میل ایڈریس',
                'Address': 'پتہ',
                'Company': 'کمپنی',
                'Website': 'ویب سائٹ',
                'Contact Person': 'رابطہ شخص',
                'Notes': 'نوٹس',
                'Opening Balance': 'ابتدائی بیلنس',
                'Save Supplier': 'سپلائر محفوظ کریں',
                'Clear': 'صاف کریں',
                'Adding...': 'شامل کر رہے ہیں...',
                'Supplier saved successfully!': 'سپلائر کامیابی سے محفوظ ہو گیا!',
                'Failed to save supplier': 'سپلائر محفوظ کرنے میں ناکامی',
                'Network error occurred': 'نیٹ ورک کی خرابی',
                'Invalid phone number': 'غلط فون نمبر',
                'Invalid email address': 'غلط ای میل ایڈریس',
                'Supplier name is required': 'سپلائر کا نام ضروری ہے',
                'Name must be at least 2 characters': 'نام کم از کم 2 حروف کا ہونا چاہیے',
                'Please enter a valid phone number': 'براہ کرم درست فون نمبر درج کریں',
                'Please enter a valid email address': 'براہ کرم درست ای میل ایڈریس درج کریں',
                'Please enter a valid website URL': 'براہ کرم درست ویب سائٹ یو آر ایل درج کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Select a shop first': 'پہلے ایک شاپ منتخب کریں',
                'Current Shop': 'موجودہ شاپ',
                'Supplier will be added to this shop': 'سپلائر اس شاپ میں شامل کیا جائے گا',
                'No shop selected. Please switch to a shop first.': 'کوئی شاپ منتخب نہیں۔ براہ کرم پہلے شاپ تبدیل کریں۔',
                'Switch Shop': 'شاپ تبدیل کریں',
                'Shop switched successfully': 'شاپ کامیابی سے تبدیل ہو گئی',
                'Failed to switch shop': 'شاپ تبدیل کرنے میں ناکامی',
                'No shops available': 'کوئی شاپ دستیاب نہیں',
                'Active': 'فعال',
                'Current': 'موجودہ',
                'Optional': 'اختیاری',
                'Opening balance will be applied to supplier account': 'ابتدائی بیلنس سپلائر اکاؤنٹ میں لگایا جائے گا',
                'Draft loaded': 'ڈرافٹ لوڈ ہو گیا',
                'Draft cleared': 'ڈرافٹ صاف ہو گیا',
                'A saved draft exists': 'ایک محفوظ ڈرافٹ موجود ہے',
                'Loading shops...': 'شاپس لوڈ ہو رہی ہیں...',
                'Suppliers List': 'سپلائرز کی فہرست'
            },
            'ar': {
                'Add Supplier': 'إضافة مورد',
                'Fill in the supplier details below': 'املأ تفاصيل المورد أدناه',
                'Supplier Name': 'اسم المورد',
                'Phone Number': 'رقم الهاتف',
                'Email Address': 'البريد الإلكتروني',
                'Address': 'العنوان',
                'Company': 'الشركة',
                'Website': 'الموقع الإلكتروني',
                'Contact Person': 'شخص الاتصال',
                'Notes': 'ملاحظات',
                'Opening Balance': 'الرصيد الافتتاحي',
                'Save Supplier': 'حفظ المورد',
                'Clear': 'مسح',
                'Adding...': 'جاري الإضافة...',
                'Supplier saved successfully!': 'تم حفظ المورد بنجاح!',
                'Failed to save supplier': 'فشل في حفظ المورد',
                'Network error occurred': 'حدث خطأ في الشبكة',
                'Invalid phone number': 'رقم هاتف غير صالح',
                'Invalid email address': 'بريد إلكتروني غير صالح',
                'Supplier name is required': 'اسم المورد مطلوب',
                'Name must be at least 2 characters': 'يجب أن يكون الاسم حرفين على الأقل',
                'Please enter a valid phone number': 'الرجاء إدخال رقم هاتف صحيح',
                'Please enter a valid email address': 'الرجاء إدخال بريد إلكتروني صحيح',
                'Please enter a valid website URL': 'الرجاء إدخال رابط موقع صحيح',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Select a shop first': 'اختر متجرًا أولاً',
                'Current Shop': 'المتجر الحالي',
                'Supplier will be added to this shop': 'سيتم إضافة المورد إلى هذا المتجر',
                'No shop selected. Please switch to a shop first.': 'لم يتم اختيار متجر. الرجاء التبديل إلى متجر أولاً.',
                'Switch Shop': 'تبديل المتجر',
                'Shop switched successfully': 'تم تبديل المتجر بنجاح',
                'Failed to switch shop': 'فشل في تبديل المتجر',
                'No shops available': 'لا توجد متاجر متاحة',
                'Active': 'نشط',
                'Current': 'حالي',
                'Optional': 'اختياري',
                'Opening balance will be applied to supplier account': 'سيتم تطبيق الرصيد الافتتاحي على حساب المورد',
                'Draft loaded': 'تم تحميل المسودة',
                'Draft cleared': 'تم مسح المسودة',
                'A saved draft exists': 'توجد مسودة محفوظة',
                'Loading shops...': 'جاري تحميل المتاجر...',
                'Suppliers List': 'قائمة الموردين'
            },
            'es': {
                'Add Supplier': 'Agregar Proveedor',
                'Fill in the supplier details below': 'Complete los detalles del proveedor a continuación',
                'Supplier Name': 'Nombre del Proveedor',
                'Phone Number': 'Número de Teléfono',
                'Email Address': 'Correo Electrónico',
                'Address': 'Dirección',
                'Company': 'Compañía',
                'Website': 'Sitio Web',
                'Contact Person': 'Persona de Contacto',
                'Notes': 'Notas',
                'Opening Balance': 'Saldo Inicial',
                'Save Supplier': 'Guardar Proveedor',
                'Clear': 'Limpiar',
                'Adding...': 'Agregando...',
                'Supplier saved successfully!': '¡Proveedor guardado exitosamente!',
                'Failed to save supplier': 'Error al guardar el proveedor',
                'Network error occurred': 'Ocurrió un error de red',
                'Invalid phone number': 'Número de teléfono inválido',
                'Invalid email address': 'Correo electrónico inválido',
                'Supplier name is required': 'El nombre del proveedor es requerido',
                'Name must be at least 2 characters': 'El nombre debe tener al menos 2 caracteres',
                'Please enter a valid phone number': 'Por favor ingrese un número de teléfono válido',
                'Please enter a valid email address': 'Por favor ingrese un correo electrónico válido',
                'Please enter a valid website URL': 'Por favor ingrese una URL de sitio web válida',
                'Success': 'Éxito',
                'Error': 'Error',
                'Warning': 'Advertencia',
                'Select a shop first': 'Seleccione una tienda primero',
                'Current Shop': 'Tienda Actual',
                'Supplier will be added to this shop': 'El proveedor se agregará a esta tienda',
                'No shop selected. Please switch to a shop first.': 'No se seleccionó ninguna tienda. Por favor cambie a una tienda primero.',
                'Switch Shop': 'Cambiar Tienda',
                'Shop switched successfully': 'Tienda cambiada exitosamente',
                'Failed to switch shop': 'Error al cambiar de tienda',
                'No shops available': 'No hay tiendas disponibles',
                'Active': 'Activo',
                'Current': 'Actual',
                'Optional': 'Opcional',
                'Opening balance will be applied to supplier account': 'El saldo inicial se aplicará a la cuenta del proveedor',
                'Draft loaded': 'Borrador cargado',
                'Draft cleared': 'Borrador limpiado',
                'A saved draft exists': 'Existe un borrador guardado',
                'Loading shops...': 'Cargando tiendas...',
                'Suppliers List': 'Lista de Proveedores'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addSupplier: 'Add Supplier',
        fillDetails: 'Fill in the supplier details below',
        supplierName: 'Supplier Name',
        phoneNumber: 'Phone Number',
        email: 'Email Address',
        address: 'Address',
        company: 'Company',
        website: 'Website',
        contactPerson: 'Contact Person',
        notes: 'Notes',
        openingBalance: 'Opening Balance',
        addSupplierButton: 'Save Supplier',
        clearButton: 'Clear',
        adding: 'Adding...',
        supplierAdded: 'Supplier saved successfully!',
        failedAdd: 'Failed to save supplier',
        networkError: 'Network error occurred',
        invalidPhone: 'Invalid phone number',
        invalidEmail: 'Invalid email address',
        nameRequired: 'Supplier name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        websiteInvalid: 'Please enter a valid website URL',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        willAddToShop: 'Supplier will be added to this shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        noShopPermission: 'You do not have permission to add suppliers to this shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        active: 'Active',
        current: 'Current',
        optional: 'Optional',
        balanceWillApply: 'Opening balance will be applied to supplier account',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        draftExists: 'A saved draft exists',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        unnamedSupplier: 'Unnamed Supplier',
        noDraftsSaved: 'No drafts saved',
        clearForm: 'Clear Form',
        viewDrafts: 'View Drafts',
        goBack: 'Go Back',
        suppliersList: 'Suppliers List'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const supplierTranslator = createSupplierTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozui_modal-overlay" onClick={onClose}>
            <div className="ozui_modal" onClick={e => e.stopPropagation()}>
                <div className="ozui_modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozui_modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozui_modal-body">
                    {loading ? (
                        <div className="ozui_loading-state">{translations.loadingShops || 'Loading shops...'}</div>
                    ) : shops.length === 0 ? (
                        <div className="ozui_empty-state">{translations.noShops || 'No shops available'}</div>
                    ) : (
                        shops.map((shop) => (
                            <div 
                                key={shop.shop_id}
                                className={`ozui_shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozui_active' : ''}`}
                                onClick={() => onSelectShop(shop)}
                            >
                                <div className="ozui_shop-info">
                                    <div className="ozui_shop-name">{shop.shop_name}</div>
                                    <div className="ozui_shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                </div>
                                {currentShop?.shop_id === shop.shop_id && (
                                    <div className="ozui_shop-badge">✓ {translations.current || 'Current'}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const AddSupplier = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    
    // Add ref for supplier name input
    const supplierNameInputRef = useRef(null);
    
    // Initial form data
    const initialFormData = {
        supplier_name: '',
        phone: '',
        email: '',
        address: '',
        company: '',
        website: '',
        contact_person: '',
        notes: '',
        opening_balance: '0.00',
    };
    
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    
    const isSubmittingRef = useRef(false);
    const toastTimerRef = useRef(null);
    const formRef = useRef(null);
    const submitButtonRef = useRef(null);
    const isInitialMount = useRef(true);
    const draftsDropdownRef = useRef(null);

    // Auto-focus on supplier name input when form is opened
    useEffect(() => {
        // Focus on the supplier name input
        if (supplierNameInputRef.current) {
            supplierNameInputRef.current.focus();
        }
    }, []);

    // Handle Tab key on any dropdown (if added later)
    const handleDropdownKeyDown = (e, closeDropdownFn) => {
        if (e.key === 'Tab') {
            closeDropdownFn();
            // Let the tab naturally move to next element
            return;
        }
    };

    // Draft Management Functions
    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `supplier_draft_${userId}_${shopId}`;
    };

    const saveDraft = () => {
        if (!formData.supplier_name && !formData.phone && !formData.email && !formData.company) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }
        
        const draftId = Date.now();
        const draft = {
            id: draftId,
            ...formData,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };
        
        const existingDrafts = JSON.parse(localStorage.getItem('supplier_drafts') || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem('supplier_drafts', JSON.stringify(limitedDrafts));
        
        showToast('success', 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem('supplier_drafts') || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    const loadDraft = (draft) => {
        setFormData({
            supplier_name: draft.supplier_name || '',
            phone: draft.phone || '',
            email: draft.email || '',
            address: draft.address || '',
            company: draft.company || '',
            website: draft.website || '',
            contact_person: draft.contact_person || '',
            notes: draft.notes || '',
            opening_balance: draft.opening_balance || '0.00',
        });
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem('supplier_drafts') || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem('supplier_drafts', JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const goToSuppliersList = () => {
        window.dispatchEvent(new CustomEvent('navigateToSuppliers', { detail: { page: 'supplierlist' } }));
    };

    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    // Add this useEffect to position the drafts dropdown
    useEffect(() => {
        if (showDraftsList && draftsDropdownRef.current) {
            const button = draftsDropdownRef.current.querySelector('.drafts-btn');
            const dropdown = draftsDropdownRef.current.querySelector('.ozui-drafts-dropdown');
            
            if (button && dropdown) {
                const rect = button.getBoundingClientRect();
                dropdown.style.position = 'fixed';
                dropdown.style.top = `${rect.bottom + 8}px`;
                dropdown.style.right = `${window.innerWidth - rect.right}px`;
            }
        }
    }, [showDraftsList]);

    // Click outside for drafts dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) {
                setShowDraftsList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // ============================================
    // THEME COLOR AUTO-SYNC WITH GRADIENT ORBS
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        console.log('Applying theme to AddSupplier form:', color);
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        
        // Update gradient orb colors dynamically
        const updateOrbColors = () => {
            // Parse color to RGB
            let r, g, b;
            if (color.startsWith('#')) {
                r = parseInt(color.slice(1, 3), 16);
                g = parseInt(color.slice(3, 5), 16);
                b = parseInt(color.slice(5, 7), 16);
            } else {
                r = 99; g = 102; b = 241; // Default fallback
            }
            
            // Create or update style for gradient orbs
            let style = document.getElementById('dynamic-orb-styles-supplier');
            if (!style) {
                style = document.createElement('style');
                style.id = 'dynamic-orb-styles-supplier';
                document.head.appendChild(style);
            }
            
            style.textContent = `
                .ozui-header-bg-animation .gradient-orb.orb-1 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-2 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-3 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-4 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-5 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
                }
            `;
        };
        
        updateOrbColors();
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        // Initial theme application
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        // Function to force update orbs
        const forceOrbUpdate = () => {
            const currentTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
            if (currentTheme !== currentThemeColor) {
                applyTheme(currentTheme);
            } else {
                // Still ensure orbs are correct
                let r, g, b;
                if (currentTheme.startsWith('#')) {
                    r = parseInt(currentTheme.slice(1, 3), 16);
                    g = parseInt(currentTheme.slice(3, 5), 16);
                    b = parseInt(currentTheme.slice(5, 7), 16);
                } else {
                    r = 99; g = 102; b = 241;
                }
                
                let style = document.getElementById('dynamic-orb-styles-supplier');
                if (!style) {
                    style = document.createElement('style');
                    style.id = 'dynamic-orb-styles-supplier';
                    document.head.appendChild(style);
                }
                
                style.textContent = `
                    .ozui-header-bg-animation .gradient-orb.orb-1 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
                    }
                    .ozui-header-bg-animation .gradient-orb.orb-2 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
                    }
                    .ozui-header-bg-animation .gradient-orb.orb-3 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
                    }
                    .ozui-header-bg-animation .gradient-orb.orb-4 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
                    }
                    .ozui-header-bg-animation .gradient-orb.orb-5 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
                    }
                `;
            }
        };
        
        // Listen for storage changes
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                console.log('Theme changed via storage:', newTheme);
                applyTheme(newTheme);
            }
        };
        
        // Listen for custom theme change event
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                console.log('Theme changed via event:', event.detail.theme);
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        // Poll for theme changes
        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentThemeColor) {
                applyTheme(storedTheme);
            }
            forceOrbUpdate();
        }, 500);
        
        // DOM observer to ensure orbs are updated after re-renders
        const domObserver = new MutationObserver(() => {
            forceOrbUpdate();
        });
        
        domObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
            domObserver.disconnect();
        };
    }, [applyTheme, currentThemeColor]);

    // ============================================
    // DARK MODE AUTO-SYNC
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            
            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }
            
            // Also apply to body for global dark mode
            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };
        
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                
                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozui-dark');
                    } else {
                        container.classList.remove('ozui-dark');
                    }
                }
                
                if (isDark) {
                    document.body.classList.add('ozui-dark');
                    document.documentElement.classList.add('ozui-dark');
                } else {
                    document.body.classList.remove('ozui-dark');
                    document.documentElement.classList.remove('ozui-dark');
                }
            } else {
                syncDarkMode();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        // Poll for dark mode changes every second (as fallback)
        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // ============================================
    // LANGUAGE AUTO-SYNC
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            console.log('Loading translations for language:', lang);
            const trans = await supplierTranslator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                console.log('Language changed via storage:', lang);
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    console.log('Language changed via event:', newLang);
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        // Initial load
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        // Poll for language changes every second (as fallback)
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL DIRECTION SYNC
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');
        
        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    const submitSupplier = async (submitData) => {
        if (isSubmittingRef.current) return false;
        isSubmittingRef.current = true;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add',
                    supplier_name: submitData.supplier_name,
                    phone: submitData.phone,
                    email: submitData.email,
                    address: submitData.address,
                    company: submitData.company,
                    website: submitData.website,
                    contact_person: submitData.contact_person,
                    notes: submitData.notes,
                    current_balance: parseFloat(submitData.opening_balance) || 0,
                    username: user?.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.supplierAdded || 'Supplier saved successfully!');
                setFormData(initialFormData);
                setErrors({});
                return true;
            } else if (data.status === 'duplicate') {
                showToast('warning', data.message || 'Duplicate submission detected');
                return false;
            } else {
                showToast('error', data.message || translations.failedAdd || 'Failed to save supplier');
                return false;
            }
        } catch (error) {
            showToast('error', translations.networkError || 'Network error occurred');
            return false;
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                setLoading(false);
            }, 1000);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmittingRef.current) return;
        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }
        if (!validateForm()) return;
        
        setLoading(true);
        await submitSupplier(formData);
        setLoading(false);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.supplier_name.trim()) {
            newErrors.supplier_name = translations.nameRequired || 'Supplier name is required';
        } else if (formData.supplier_name.trim().length < 2) {
            newErrors.supplier_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }
        if (formData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(formData.phone)) {
            newErrors.phone = translations.phoneInvalid || 'Please enter a valid phone number';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = translations.emailInvalid || 'Please enter a valid email address';
        }
        if (formData.website && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.website)) {
            newErrors.website = translations.websiteInvalid || 'Please enter a valid website URL';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleClear = () => {
        setFormData(initialFormData);
        setErrors({});
        showToast('success', translations.draftCleared || 'Form cleared');
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
                setShowShopSelector(false);
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };
        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="ozui-shop-indicator no-shop">
                    <div className="ozui-shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="ozui-shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="ozui-header-shop-btn"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        
    };

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Modern Animated Sticky Header */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{translations.addSupplier || 'Add Supplier'}</h1>
                            <p>{translations.fillDetails || 'Fill in the supplier details below'}</p>
                        </div>
                    </div>
                    
                    <div className="ozui-header-right">
                        {renderShopIndicator()}
                        
                        <div className="ozui-header-actions">
                            <button 
                                className="ozui-action-btn clear-draft" 
                                onClick={handleClear}
                                disabled={loading}
                                title={translations.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{translations.clearButton || 'Clear'}</span>
                            </button>
                            
                            <button 
                                className="ozui-action-btn save-draft" 
                                onClick={saveDraft}
                                disabled={loading}
                                title={translations.saveDraft || 'Save as Draft'}
                            >
                                <FontAwesomeIcon icon={faFileAlt} />
                                <span>{translations.saveDraft || 'Save Draft'}</span>
                            </button>
                            
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button 
                                    className="ozui-action-btn drafts-btn" 
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations.viewDrafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.supplier_name || translations.unnamedSupplier || 'Unnamed Supplier'}
                                                            </div>
                                                            <div className="draft-details">
                                                                {draft.phone && <span>{draft.phone}</span>}
                                                                {draft.company && <span>{draft.company}</span>}
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button 
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.supplier_name ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={supplierNameInputRef}
                                type="text"
                                name="supplier_name"
                                id="supplier_name"
                                value={formData.supplier_name}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                autoFocus
                            />
                            <label htmlFor="supplier_name">
                                <FontAwesomeIcon icon={faUser} />
                                {translations.supplierName || 'Supplier Name'} <span>*</span>
                            </label>
                        </div>
                        {errors.supplier_name && <small>{errors.supplier_name}</small>}
                    </div>

                    <div className={`ozui-field ${errors.phone ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="phone">
                                <FontAwesomeIcon icon={faPhone} />
                                {translations.phoneNumber || 'Phone Number'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.phone && <small>{errors.phone}</small>}
                    </div>

                    <div className={`ozui-field ${errors.email ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="email">
                                <FontAwesomeIcon icon={faEnvelope} />
                                {translations.email || 'Email Address'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.email && <small>{errors.email}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="company"
                                id="company"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="company">
                                <FontAwesomeIcon icon={faBuilding} />
                                {translations.company || 'Company'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="contact_person"
                                id="contact_person"
                                value={formData.contact_person}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="contact_person">
                                <FontAwesomeIcon icon={faUserTag} />
                                {translations.contactPerson || 'Contact Person'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                    <div className={`ozui-field ${errors.website ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="website"
                                id="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="website">
                                <FontAwesomeIcon icon={faGlobe} />
                                {translations.website || 'Website'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.website && <small>{errors.website}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="address"
                                id="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="address">
                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                {translations.address || 'Address'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="opening_balance"
                                id="opening_balance"
                                value={formData.opening_balance}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                step="0.01"
                            />
                            <label htmlFor="opening_balance">
                                <FontAwesomeIcon icon={faMoneyBillWave} />
                                {translations.openingBalance || 'Opening Balance'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                   
                </div>
                
                {/* Form Actions */}
                <div className="ozui-form-actions">
                    <button 
                        ref={submitButtonRef}
                        type="submit" 
                        className="ozui-btn ozui-btn-primary" 
                        onClick={handleSubmit}
                        disabled={loading || !currentShop}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations.adding || 'Adding...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{translations.addSupplierButton || 'Save Supplier'}</span>
                            </>
                        )}
                    </button>
                    
                
                </div>
            </form>
        </div>
    );
};

export default AddSupplier;